import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import NOCChatComponent from "../../../components/NOCChat";
import {
  clearCurrentRequest,
  fetchNOCHigherEducationRequest,
  INocHigherEducationRequest,
} from "../../../core/data/redux/nocHigherEducationSlice";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import type { RootState } from "../../../core/data/redux/store";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import { API_URLS, BASE_URLS } from "../../../core/data/redux/apis/urls";
import Api from "../../../core/data/redux/utils/apiCall";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";
import { all_routes } from "../../router/all_routes";
import { handleDocumentClick as handleDocumentClickUtil } from "../../../utils/documentUtils";

import {
  SapRequestStatus,
  getRequestStatusBadgeClass as getGlobalStatusBadgeClass,
  getRequestStatusLabel as getGlobalStatusLabel,
} from "../../../core/data/constants/requestStatus";

interface NOCHigherEducationDetailsProps { }

const NOCHigherEducationDetails = (props: NOCHigherEducationDetailsProps) => {
  const routes = all_routes;
  const { id } = useParams();
  const dispatch = useDispatch<any>();

  // Get data from Redux state
  const { currentRequest, loading } = useSelector(
    (state: RootState) => state.nocHigherEducationState
  );
  const employeesState = useSelector((state: any) => state.employeesState);

  // Local state for application data and download state
  const [applicationData, setApplicationData] =
    useState<INocHigherEducationRequest | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Load profile
  useEffect(() => {
    dispatch(getMyProfile({}));
  }, [dispatch]);

  // Load data from Redux state
  useEffect(() => {
    if (id) {
      dispatch(fetchNOCHigherEducationRequest({ id: parseInt(id) }));
    }

    return () => {
      dispatch(clearCurrentRequest());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (currentRequest) {
      setApplicationData(currentRequest);
    }
  }, [currentRequest]);

  // Helper functions
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "-";
    }
  };

  const formatYesNo = (value: string | undefined | null) => {
    if (!value) return "-";
    const lowerValue = value.toLowerCase();
    if (lowerValue === "y" || lowerValue === "yes") return "Yes";
    if (lowerValue === "n" || lowerValue === "no") return "No";
    return value;
  };

  const getNatureOfDutyText = (code: string | undefined | null) => {
    if (!code) return "-";
    if (code === "G") return "General";
    if (code === "S") return "Shift";
    if (code === "01") return "Fee Receipt";
    if (code === "02") return "Prof of Payment in INR";
    if (code === "03") return "Completion Certificate";
    return code;
  };

  const countDurationYear = (months: any, years: any) => {
    const m = Number(months) || 0;
    const y = Number(years) || 0;
    if (m > 11) {
      return y + Math.floor(m / 12);
    } else {
      return y;
    }
  };

  const countDurationMonth = (months: any) => {
    const m = Number(months) || 0;
    if (m > 11) {
      return m % 12;
    } else {
      return m;
    }
  };

  const getStatusBadgeClass = (status: string | null | undefined, isDraft?: boolean) => {
    if (isDraft) return "badge bg-secondary-subtle text-secondary border border-secondary-subtle";
    if (!status) return "badge bg-secondary-subtle text-secondary border border-secondary-subtle";
    const code = status.toString().trim().toUpperCase();
    if (code === "D") return "badge bg-success-subtle text-success border border-success-subtle";
    return getGlobalStatusBadgeClass(code);
  };

  const getNOCStatusText = (status: string | null | undefined, isDraft?: boolean) => {
    if (isDraft) return "Draft";
    if (!status) return "-";
    const code = status.toString().trim().toUpperCase();
    if (code === SapRequestStatus.APPROVED) return "Approved";
    if (code === "D") return "Approved";
    if (code === SapRequestStatus.REJECTED) return "Rejected";
    return getGlobalStatusLabel(code);
  };

  const handleDocumentClick = (docParam: any) => {
    handleDocumentClickUtil(docParam, "NOC_HIGHERSTUDY");
  };


  const handleDownloadPDF = async () => {
    if (!applicationData || isDownloading) return;
    try {
      setIsDownloading(true);
      const payload = {
        empId: applicationData.empId || (applicationData as any).empID,
        type: "ZHIS",
        referenceNumber: applicationData.referenceNumber,
      };

      const res = await Api.post(
        API_URLS.NOC.HIGHER_EDUCATION_DOWNLOAD,
        payload
      );

      processNocDownloadResponse(res?.data);
    } catch (err: any) {
      console.error("Higher Studies NOC Download Error:", err);
      toast.error(err?.message || "Failed to download NOC document.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            NOC request not found. Please go back to the main page and try
            again.
          </div>
          <Link to={routes.noc.higherEducation} className="btn btn-primary">
            <i className="ti ti-arrow-left me-2"></i>
            Back to NOC List
          </Link>
        </div>
      </div>
    );
  }

  const formatDisplayValue = (val: any): string => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "object") {
      return (
        val.designation_name ||
        val.department_name ||
        val.name ||
        val.title ||
        "-"
      );
    }
    return String(val);
  };

  const profileUser = employeesState?.data?.data;
  const profileDetails = profileUser?.details || profileUser || {};

  const appAny = applicationData as any;
  const empObj =
    appAny.employee ||
    appAny.employeeDetails ||
    appAny.applicantDetails ||
    appAny.applicant ||
    appAny.emp_details ||
    appAny.userDetails ||
    profileUser ||
    {};

  const empDetails = empObj.details || empObj || {};
  const fallbackObj =
    (empDetails.firstname || empDetails.fullName || empDetails.empId)
      ? empDetails
      : profileDetails;

  const sapInfo =
    fallbackObj.sap_department_designation_wings ||
    empDetails.sap_department_designation_wings ||
    profileDetails.sap_department_designation_wings ||
    {};

  const displayStatus =
    applicationData.requestStatus ||
    applicationData.status ||
    (applicationData.is_draft ? "Draft" : "Submitted");

  const rawEmployeeName =
    (fallbackObj.firstname
      ? `${fallbackObj.firstname} ${fallbackObj.secondname || ""} ${fallbackObj.lsastname || fallbackObj.lastname || ""}`
        .replace(/\s+/g, " ")
        .trim()
      : "") ||
    fallbackObj.fullName ||
    fallbackObj.name ||
    appAny.employeeName ||
    "";
  const employeeName = formatDisplayValue(rawEmployeeName);

  const rawDesignation =
    sapInfo.designation_name ||
    fallbackObj.designation ||
    fallbackObj.designation_details?.designationName ||
    (fallbackObj.designationID ? `Designation ID: ${fallbackObj.designationID}` : "") ||
    appAny.designation;
  const designation = formatDisplayValue(rawDesignation);

  const rawDepartment =
    sapInfo.department_name ||
    fallbackObj.department_name ||
    fallbackObj.department_details?.departmentName ||
    (fallbackObj.department ? `Department ID: ${fallbackObj.department}` : "") ||
    appAny.department;
  const department = formatDisplayValue(rawDepartment);

  const isApproved = String(applicationData?.requestStatus || "").toUpperCase() === "D";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.higherEducation}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <div>
                <h3 className="page-title mb-1">
                  NOC for Higher Education - Details
                </h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={routes.noc.main}>NOC Services</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={routes.noc.higherEducation}>Higher Education</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Details
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>

          {isApproved && (
            <button
              className="btn btn-success ms-auto"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              <i className="ti ti-download me-2"></i>
              {isDownloading ? "Downloading..." : "Download Digital NOC"}
            </button>
          )}
        </div>

        {/* Chat Component */}
        {applicationData.id && (
          <NOCChatComponent
            nocRequestId={applicationData.id}
            nocType="Higher Education"
            employeeName={employeeName}
            employeeId={applicationData.empId}
            employeeImage={empDetails?.image}
            approverId={
              applicationData.approverUserID ||
              (applicationData as any).approverId ||
              (applicationData as any).reportingManagerID
            }
          />
        )}

        <div className="row mt-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">Application Details</h4>
                  <p className="text-muted mb-0 mt-1">
                    Reference Number: {applicationData.referenceNumber || "-"}
                  </p>
                  {appAny.cancellationFlag && (
                    <p className="text-danger mb-0 mt-1">
                      Reference Number of NOC request to be Cancelled:{" "}
                      {appAny.referenceNumber1 || "-"}
                    </p>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary fs-14">
                    {appAny.cancellationFlag
                      ? "NOC Request Cancellation"
                      : "NOC Request"}
                  </span>
                  <span
                    className={`badge ${getStatusBadgeClass(displayStatus, applicationData.is_draft)} fs-14`}
                  >
                    {getNOCStatusText(displayStatus, applicationData.is_draft)}
                  </span>
                </div>
              </div>

              <div className="card-body">
                {/* 1. Employee Details Section */}
                <h5 className="text-dark fw-medium mb-3">
                  <i className="ti ti-user me-2"></i>
                  Employee Information
                </h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Employee Name</small>
                      <div className="fw-bold text-dark fs-15">{employeeName}</div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                      <div className="fw-bold text-primary fs-15">
                        {formatEmpIdForDisplay(
                          applicationData.empId ||
                          (applicationData as any).empID ||
                          (applicationData as any).employee?.empId ||
                          (applicationData as any).employee?.empID ||
                          fallbackObj.empId ||
                          fallbackObj.empID ||
                          profileDetails.empId ||
                          profileDetails.empID ||
                          "-"
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                      <div className="fw-bold text-dark fs-15">{designation}</div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                      <div className="fw-bold text-dark fs-15">{department}</div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining in DMRC</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(fallbackObj.date_of_joining || empObj.date_of_joining)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(
                          fallbackObj.employmentType ||
                          empObj.employmentType ||
                          empDetails.employmentType ||
                          appAny.employee?.employmentType ||
                          appAny.employee?.details?.employmentType ||
                          appAny.employmentType ||
                          fallbackObj.empStatus ||
                          appAny.employmentStatus
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Contact Number</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(fallbackObj.phone || fallbackObj.mobileNumber || appAny.contactNumber || empObj.phone)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(fallbackObj.email || appAny.emailId || empObj.email)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Currently With (Approver)</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(applicationData.approverName)}{" "}
                        {applicationData.approverUserID
                          ? `(${formatEmpIdForDisplay(applicationData.approverUserID)})`
                          : ""}
                      </div>
                    </div>
                  </div>

                  {/* <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Date of Birth</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(fallbackObj.birthdate || empObj.birthdate || appAny.birthdate || appAny.dateOfBirth)}
                      </div>
                    </div>
                  </div> */}

                  {/* <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Age</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(appAny.age || fallbackObj.age || empObj.age || empDetails.age)}
                      </div>
                    </div>
                  </div> */}

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Request Creation Date</small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(
                          applicationData.requestCreationDate || applicationData.created_at
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                {/* 2. Course Details Section */}
                <h5 className="text-dark fw-medium mb-3">
                  <i className="ti ti-book me-2"></i>
                  Course Details
                </h5>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Whether probation is confirmed in DMRC
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatYesNo(appAny.probationConfirmed)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Whether currently pursuing any higher education course, for which NOC from DMRC was obtained
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatYesNo(appAny.currentlyPursuingA)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Nature of Duty performing
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {getNatureOfDutyText(
                          appAny.natureOfDutyPerfo ||
                          appAny.nature_of_duty?.nature_of_duty_id
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Name of the study course
                      </small>
                      <div className="fw-bold text-primary fs-15">
                        {formatDisplayValue(applicationData.courseName)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Type of Course
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(
                          applicationData.course_type?.course_type_title ||
                          appAny.otherThanDiploma ||
                          applicationData.diplomaOrDegree
                        )}
                      </div>
                    </div>
                  </div>

                  {appAny.course_type?.course_type_id === "O" && (
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">
                          Specify type of course
                        </small>
                        <div className="fw-bold text-dark fs-15">
                          {formatDisplayValue(appAny.otherThanDiploma)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Duration of course
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {countDurationYear(
                          appAny.monthsCourseDuration,
                          appAny.durationYears
                        )}
                        Y {countDurationMonth(appAny.monthsCourseDuration)}M
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Date of joining course
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(appAny.dateOfCourseJoining)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Course ending date (tentative)
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(appAny.courseEndingDate)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Name of institution / university
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(applicationData.institute)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Mode of Course
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(
                          appAny.mode_of_course?.course_mode_title ||
                          applicationData.partTimeOrCorresp
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Last date of registration for course
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDate(appAny.lastDateOfRegistration)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Timing of classes
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(appAny.timingOfClasses)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Whether the institution is approved by concerned government body like UGC/AICTE/DEC
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatYesNo(appAny.isGovernmentBody)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Whether the course is approved by concerned government body such as UGC/AICTE/DEC
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatYesNo(appAny.isCourseRecogByGov)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Is course relevant to your employment
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatYesNo(appAny.isReleventToEmployee)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Whether any other NOC has been granted during employment in DMRC for any other course
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatYesNo(appAny.whetherAnotherNoc)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Proposed course is pursuing in
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {appAny.inIndiaOrNot === "I" || appAny.inIndiaOrNot === "Y"
                          ? "India"
                          : appAny.inIndiaOrNot === "O" || appAny.inIndiaOrNot === "N"
                            ? "Outside India"
                            : "-"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <div className="p-3 bg-light rounded border h-100">
                      <small className="text-muted d-block uppercase fw-medium mb-1">
                        Remarks
                      </small>
                      <div className="fw-bold text-dark fs-15">
                        {formatDisplayValue(
                          appAny.remarks ||
                          appAny.anyRemarks ||
                          applicationData.remarks ||
                          appAny.Remarks
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Attachments & Guidance Section */}
                <hr className="my-4" />
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <h5 className="text-dark fw-medium mb-3">
                      <i className="ti ti-paperclip me-2"></i>
                      Attachments
                    </h5>

                    <div className="alert alert-danger-light border border-danger rounded mb-3">
                      <h6 className="text-danger fw-bold mb-2">
                        <i className="ti ti-info-circle me-2"></i>
                        Mandatory Attachments Instructions
                      </h6>
                      <ul className="mb-0 text-muted small">
                        <li>
                          - In case any other NOC has been granted for pursuing higher
                          studies during course of employment in DMRC, attach copy of NOC.
                        </li>
                        <li>
                          - Document indicating last date of registration of course must be attached.
                        </li>
                        <li>- Last date of admission in brochure.</li>
                      </ul>
                    </div>

                    {appAny.documents && appAny.documents.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>File Name</th>
                              <th>Document Type</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {appAny.documents
                              .filter(
                                (item: any) =>
                                  item.referenceNumber?.toString().length < 20 ||
                                  !item.referenceNumber
                              )
                              .map((doc: any, index: number) => (
                                <tr key={doc.id || index}>
                                  <td>{index + 1}</td>
                                  <td className="fw-medium">
                                    {doc.fileName || doc.serverKey || "Document"}
                                  </td>
                                  <td>
                                    <span className="badge bg-info-transparent text-info">
                                      {doc.typeOfAttachedDoc ||
                                        doc.fileExtension ||
                                        "Document"}
                                    </span>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleDocumentClick(doc)}
                                    >
                                      <i className="ti ti-eye me-1"></i>
                                      View Document
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-muted small">No documents attached.</div>
                    )}
                  </div>
                </div>

                {/* 4. HR Remarks */}
                {appAny.hRemarks &&
                  appAny.hRemarks !== "X" &&
                  String(appAny.hRemarks).trim() !== "" &&
                  String(appAny.hRemarks).trim() !== String(appAny.remarks || "").trim() && (
                  <>
                    <hr className="my-4" />
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <h5 className="text-dark fw-medium mb-3">
                          <i className="ti ti-message me-2"></i>
                          HR Remarks
                        </h5>
                        <div className="mb-3">
                          <span className="text-muted d-block fw-medium">HR / Approver Remarks:</span>
                          <p className={`mb-0 mt-1 ${appAny.requestStatus === "R" || appAny.status === "R" || displayStatus?.toLowerCase() === "rejected"
                            ? "text-danger fw-bold"
                            : "text-dark"
                            }`}>
                            {appAny.hRemarks}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* 5. Remarks History */}
                {(() => {
                  let rawList: any[] = [
                    ...(Array.isArray(appAny?.remarks_data) && appAny.remarks_data.length > 0
                      ? appAny.remarks_data
                      : Array.isArray(appAny?.history) && appAny.history.length > 0
                      ? appAny.history
                      : Array.isArray(appAny?.work_history) && appAny.work_history.length > 0
                      ? appAny.work_history
                      : Array.isArray(appAny?.remarksHistory) && appAny.remarksHistory.length > 0
                      ? appAny.remarksHistory
                      : [])
                  ].map((item: any) => ({ ...item }));

                  const applicantId = String(
                    appAny?.empId ||
                    appAny?.empID ||
                    appAny?.employee?.empId ||
                    appAny?.employee?.empID ||
                    ""
                  ).replace(/^0+/, "");

                  const hasEmployeeEntry = rawList.some((item: any) => {
                    const itemUserId = String(
                      item?.approverUserID || item?.userId || item?.empID || ""
                    ).replace(/^0+/, "");
                    return (
                      item?.usertype === "E" ||
                      item?.usertype === "Employee" ||
                      item?.approverName === "User" ||
                      (applicantId && itemUserId && itemUserId === applicantId)
                    );
                  });

                  const employeeRemarks = (() => {
                    const candidates = [
                      appAny?.anyRemarks,
                      appAny?.any_remarks,
                      appAny?.employeeRemarks,
                      appAny?.empRemarks,
                    ];
                    for (const cand of candidates) {
                      if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
                        return cand.trim();
                      }
                    }
                    return "";
                  })();

                  const employeeTimestamp =
                    appAny?.requestCreationDate ||
                    appAny?.created_at ||
                    appAny?.createdAt ||
                    appAny?.sap_created_at ||
                    appAny?.sapCreatedAt ||
                    appAny?.appliedDate ||
                    null;

                  const employeeName = (() => {
                    if (appAny?.employee) {
                      const fname = appAny.employee.firstname || appAny.employee.firstName || "";
                      const lname = appAny.employee.lsastname || appAny.employee.lastname || appAny.employee.lastName || "";
                      const full = `${fname} ${lname}`.trim();
                      if (full) return full;
                    }
                    return appAny?.employeeName || appAny?.empName || "Employee";
                  })();

                  const employeeUserId = appAny?.empId || appAny?.empID || appAny?.employee?.empId || "";

                  if (hasEmployeeEntry) {
                    rawList = rawList.map((item: any) => {
                      const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                      const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                      if (isEmp && !item?.remarks && employeeRemarks) {
                        return { ...item, remarks: employeeRemarks };
                      }
                      return { ...item };
                    });
                  } else if (appAny && (employeeUserId || employeeName !== "Employee" || appAny?.id || appAny?.referenceNumber)) {
                    rawList.push({
                      usertype: "Employee",
                      approverUserID: employeeUserId,
                      userId: employeeUserId,
                      empID: employeeUserId,
                      approverName: employeeName,
                      name: employeeName,
                      remarks: employeeRemarks,
                      createdAt: employeeTimestamp,
                      isEmployee: true,
                    });
                  }

                  if (rawList.length === 0) return null;

                  return (
                    <>
                      <hr className="my-4" />
                      <div className="row">
                        <div className="col-md-12 mb-3">
                          <h5 className="text-dark fw-medium mb-3">
                            <i className="ti ti-history me-2"></i>
                            Previous Remarks History
                          </h5>
                          <div className="d-flex flex-column gap-3">
                            {rawList.map((item: any, index: number) => {
                              const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                              const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                              return (
                                <div
                                  key={index}
                                  className={`p-3 bg-white rounded border border-start-4 ${isEmp ? "border-start-info" : "border-start-primary"} shadow-xs`}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <div className={`avatar avatar-sm ${isEmp ? "bg-info-transparent text-info" : "bg-primary-transparent text-primary"} rounded-circle fw-bold`}>
                                        <i className={`${isEmp ? "ti ti-user" : "ti ti-user-check"} fs-16`}></i>
                                      </div>
                                      <div>
                                        <div className="d-flex align-items-center gap-2">
                                          <h6 className="mb-0 text-dark fw-bold">
                                            {item.approverName ||
                                              item.history_approver?.name ||
                                              item.name ||
                                              (isEmp ? "Employee" : "Approver")}
                                          </h6>
                                          <span className={`badge ${isEmp ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"} border text-xs`}>
                                            {isEmp ? "Employee" : "Approver"}
                                          </span>
                                        </div>
                                        <small className="text-muted">
                                          ID:{" "}
                                          {formatEmpIdForDisplay(
                                            item.approverUserID ||
                                            item.history_approver?.empId ||
                                            item.userId ||
                                            (isEmp ? employeeUserId : "")
                                          ) || "-"}
                                        </small>
                                      </div>
                                    </div>
                                    <span className="badge bg-light text-muted border">
                                      <i className="ti ti-calendar me-1"></i>
                                      {formatDate(
                                        item.createdAt ||
                                        item.created_at ||
                                        item.timeStamp
                                      )}
                                      {(item.createdAt || item.created_at || item.timeStamp) && !isNaN(new Date(item.createdAt || item.created_at || item.timeStamp).getTime())
                                        ? " " + new Date(item.createdAt || item.created_at || item.timeStamp).toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          second: "2-digit",
                                          hour12: true,
                                        }).toUpperCase()
                                        : ""}
                                    </span>
                                  </div>
                                  <div className="mt-2 pt-2 border-top">
                                    <p className="mb-0 text-dark fw-medium fs-14">
                                      {(() => {
                                        const rem = item.remarks;
                                        const isEmpRemarkCopied =
                                          !isEmp &&
                                          employeeRemarks &&
                                          String(rem).trim() === String(employeeRemarks).trim();
                                        const hasValidRemark =
                                          rem &&
                                          String(rem).trim() !== "-" &&
                                          String(rem).trim() !== "X" &&
                                          !isEmpRemarkCopied;

                                        if (hasValidRemark) {
                                          return (
                                            <span>
                                              <i className="ti ti-quote text-muted me-1"></i>
                                              {rem}
                                            </span>
                                          );
                                        }
                                        return <span className="text-muted fst-italic">No remarks</span>;
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* 5. Declaration & Notes */}
                <hr className="my-4" />
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="card bg-light border-0">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-2">
                          <i className="ti ti-checkbox me-2 text-success"></i>
                          Declaration
                        </h6>
                        <p className="mb-0 text-muted small">
                          I hereby declare that the information given above is true
                          and my duty hours in DMRC would not get disturbed in any case.
                          I may kindly be granted permission to pursue further studies.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="card bg-light border-0">
                      <div className="card-body">
                        <h6 className="fw-bold text-dark mb-2">
                          <i className="ti ti-alert-circle me-2 text-warning"></i>
                          Mandatory Condition / Note
                        </h6>
                        <p className="mb-0 text-muted small">
                          Condition: Minimum 03 years regular service in DMRC dispensed with (as per Master Circular).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="d-flex justify-content-between">
                  <Link
                    to={routes.noc.higherEducation}
                    className="btn btn-outline-primary"
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to List
                  </Link>
                  {applicationData.is_draft && (
                    <Link
                      to={`${routes.noc.higherEducation}/edit/${applicationData.id}`}
                      className="btn btn-primary"
                    >
                      <i className="ti ti-edit me-2"></i>
                      Edit Request
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NOCHigherEducationDetails;

